import styled from '@emotion/styled/macro';
import { FilterMethod, ICustomFilter, LoadingMeter, TableStorage } from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { handleError, IInjectedRootStore } from 'stores';

export const Wrapper = styled.section`
    height: 100%;
    display: flex;
    align-items: center;
`;

export const StyledLoadingMeter = styled(LoadingMeter)`
    background: none;
    display: block;
    align-self: unset;
`;

interface IByFilterParams {
    filterType: 'patient' | 'seller' | 'guarantor' | 'provider' | 'payer' | 'facility' | 'subscriber';
    id: string;
}

export const ByFilter: React.FunctionComponent<IInjectedRootStore> = inject('rootStore')(
    observer(
        (props: IInjectedRootStore): React.ReactElement => {
            const { rootStore } = props;
            const { filterType, id } = useParams() as unknown as IByFilterParams;
            const navigate = useNavigate();
            useEffect(() => {
                (async () => {
                    if (id) {
                        try {
                            const getLabel = async (): Promise<string> => {
                                switch (filterType) {
                                    case 'patient':
                                        const patient = await rootStore.claimStore.loadPatientById(id);
                                        return patient ? `${patient?.first} ${patient?.last}` : id;
                                    case 'seller':
                                        const seller = await rootStore.claimStore.loadSellerById(id);
                                        return seller ? `${seller?.first} ${seller?.last}` : id;
                                    case 'guarantor':
                                        const guarantor = await rootStore.claimStore.loadGuarantorById(id);
                                        return guarantor ? `${guarantor?.first} ${guarantor?.last}` : id;
                                    case 'provider':
                                        const provider = await rootStore.claimStore.loadProviderById(id);
                                        if (!provider) {
                                            return id;
                                        }

                                        const name = _.compact([provider?.first, provider?.last]).join(' ');
                                        const organisation = provider?.organization;

                                        return name || organisation || id;
                                    case 'payer':
                                        const payer = await rootStore.claimStore.loadPayerById(id);
                                        return payer ? `${payer?.name}` : id;
                                    case 'facility':
                                        const facility = await rootStore.claimStore.loadFacilityById(id);
                                        return facility ? `${facility?.name}` : id;
                                    case 'subscriber':
                                        const subscriber = await rootStore.claimStore.loadSubscriberById(id);
                                        return subscriber ? `${subscriber?.first} ${subscriber?.last}` : id;
                                    default:
                                        return '';
                                }
                            };
                            const getFilterId = (): string => {
                                switch (filterType) {
                                    case 'patient':
                                        return 'filterPatientId';
                                    case 'seller':
                                        return 'filterSellerId';
                                    case 'guarantor':
                                        return 'filterGuarantorId';
                                    case 'provider':
                                        return 'filterProviderId';
                                    case 'payer':
                                        return 'filterPayerId';
                                    case 'facility':
                                        return 'filterFacilityId';
                                    case 'subscriber':
                                        return 'filterSubscriberId';
                                    default:
                                        return '';
                                }
                            };
                            const label = await getLabel();
                            if (label) {
                                const table = 'claim-listing-table';
                                const newFilters: ICustomFilter[] = [
                                    {
                                        filterType: 'AsyncSelect',
                                        filterId: getFilterId(),
                                        filterMethod: FilterMethod.EQ,
                                        value: {
                                            label,
                                            value: id,
                                        },
                                    },
                                ];

                                TableStorage.saveItem(table, 'filters', newFilters);
                            }
                        } catch (err) {
                            await handleError(err);
                            const message = err.payload.message || _.first(_.values(err.payload.errors));
                            window.prompt(message, err.payload.trace_id);
                        }
                    }
                    navigate('/claims', { replace: true });
                })();
            }, [id, filterType, rootStore]);
            return (
                <Wrapper>
                    <StyledLoadingMeter />
                </Wrapper>
            );
        },
    ),
);
